import * as React from 'react';
import {Button, Checkbox, Icon, Input, Select, Spin, Popover, message} from "antd";
import {IBannerTemplate} from "../store/banner/template/types";
import {selectActiveBannerTemplate} from "../store/banner/template/selectors";
import {AppState} from "../store";
import {Dispatch} from "redux";
import {withAuthorization} from "../firebase/withAuthorization";
import {RouteComponentProps, withRouter} from "react-router";
import {connect} from "react-redux";
import remergeCondition from "../firebase/conditions";
import BannerPreviewer from "../components/BannerEditor/BannerPreviewer";
import {BannerUploadError, BannerUploadType, needsSsoLogin, SsoPrompt, uploadBanner} from "../api/bannerUploadApi";
import styles from '../components/BannerEditor/BannerPreviewer.module.less'

const QRCode = require('qrcode.react');

enum PreviewLinkType {
    preview,
    newPreview,
    raw
}

const bucketName = "assets.remerge.io";
const objectKey = (s3Folder: string, type: PreviewLinkType) => {
    if (type == PreviewLinkType.preview) {
        return `dpa/${s3Folder}/preview.html`;
    }
    else if (type == PreviewLinkType.newPreview) {
        // return `dpa/ned_test/a30.html`;
        return `dpa/${s3Folder}/newPreview.html`;    
    }
    return `dpa/${s3Folder}/raw.html`;
}

interface IMatchParams {
}

interface IOwnProps extends RouteComponentProps<IMatchParams> {
    output: string;
}

interface IStateProps {
    activeBannerTemplate: IBannerTemplate,
}

interface IDispatchProps {
}

const mapStateToProps = (state: AppState, props: IOwnProps): IStateProps => ({
    activeBannerTemplate: selectActiveBannerTemplate(state),
});

const mapDispatchToProps = (
    dispatch: Dispatch
): IDispatchProps => {
    return {};
};

type IProps = IOwnProps & IStateProps & IDispatchProps

interface IState {
    selectedSizes: string[];
    uploading?: BannerUploadType;
}

class BannerPreviewerContainer extends React.Component<IProps, IState> {
    state: IState;

    constructor(pros: IProps) {
        super(pros);
        this.state = {
            selectedSizes: [],
        }
    }

    previewLink = (type: PreviewLinkType) => {
        let previewLink = "";
        if (this.props.activeBannerTemplate.s3Folder) {
            previewLink = `https://${bucketName}/${objectKey(this.props.activeBannerTemplate.s3Folder, type)}`
        }
        return previewLink
    }

    onSelectSizes = (selectedSizes: string[]) => {
        this.setState({
            selectedSizes: selectedSizes
        })

    };

    downloadPreview = () => {
        const element = document.createElement("a");
        const file = new Blob([genTemplate(this.state.selectedSizes, this.props.output)], {type: 'text/html'});
        element.href = URL.createObjectURL(file);
        element.download = "preview.html";
        document.body.appendChild(element); // Required for this to work in FireFox
        element.click();
    };

    upload = (type: BannerUploadType, body: string) => {
        const s3Folder = this.props.activeBannerTemplate.s3Folder;
        if (!s3Folder || this.state.uploading) {
            return
        }
        // Open the SSO approval window inside the click handler so it isn't popup-blocked.
        const popup = needsSsoLogin() ? window.open("about:blank", "aws-sso", "width=600,height=720") : null;
        this.setState({uploading: type});
        const hideLoading = message.loading("Uploading...", 0);
        uploadBanner(s3Folder, type, body, popup, (prompt: SsoPrompt) => {
            message.info(prompt.popupBlocked
                ? <span>Approve the AWS SSO login (code {prompt.userCode}): <a href={prompt.verificationUri} target="_blank" rel="noopener noreferrer">open AWS SSO</a></span>
                : `Approve the AWS SSO login in the popup (code ${prompt.userCode})`, 10);
        }).then(result => {
            message.success(<span>Uploaded <a href={result.url} target="_blank" rel="noopener noreferrer">{result.key}</a></span>, 5);
        }).catch((e: Error) => {
            const code = e instanceof BannerUploadError ? ` (${e.code})` : "";
            message.error(`Upload failed${code}: ${e.message}`, 8);
        }).finally(() => {
            hideLoading();
            this.setState({uploading: undefined});
        });
    };

    onSavePreview = () => {
        this.upload("preview", genTemplate(this.state.selectedSizes, this.props.output));
    };

    onSaveNewPreview = () => {
        this.upload("newPreview", genNewTemplate(this.state.selectedSizes, this.props.output));
    };

    onSaveRaw = () => {
        this.upload("raw", this.props.output);
    };

    render() {
        // console.log("TemplateEditor render........");
        const previewLink = this.previewLink(PreviewLinkType.preview)
        const newPreviewLink = this.previewLink(PreviewLinkType.newPreview)
        const rawLink = this.previewLink(PreviewLinkType.raw)

        return (
            <React.Fragment>
                <div>
                    <Button className={styles.actionButton} onClick={this.downloadPreview}>Download Preview</Button>
                    {this.props.activeBannerTemplate.s3Folder && <Button className={styles.actionButton}  onClick={this.onSaveRaw} loading={this.state.uploading === "raw"}>Upload Raw</Button>}
                    {rawLink &&
                    <Popover content={<div><QRCode value={rawLink}/>
                        <div>{rawLink} <a href={rawLink}><Icon type="link"/></a></div>
                    </div>}>
                        <Icon type="qrcode" style={{marginRight: 5}}/>
                    </Popover>
                    }
                </div>
                <div>
                    {this.props.activeBannerTemplate.s3Folder ?
                        <Button className={styles.actionButton}  onClick={this.onSavePreview} icon={'upload'} loading={this.state.uploading === "preview"}>Upload Preview</Button> :
                        <span>Provide the S3 folder first.</span>
                    }
                    {previewLink &&
                        <a className={styles.previewLink} href={previewLink} target="_blank">
                            <Button className={styles.actionButton}  icon={'link'}>Preview</Button>
                        </a>
                    }
                </div>
                <div>
                    {this.props.activeBannerTemplate.s3Folder ?
                        <Button className={styles.actionButton}  onClick={this.onSaveNewPreview} icon={'upload'} loading={this.state.uploading === "newPreview"}>Upload New Preview</Button> :
                        <span>Provide the S3 folder first.</span>
                    }
                    {newPreviewLink &&
                        <a className={styles.previewLink} href={newPreviewLink} target="_blank">
                        <Button className={styles.actionButton}  icon={'link'}>New Preview</Button>
                    </a>
                    }
                </div>
                <BannerPreviewer output={this.props.output} onSelectSizes={this.onSelectSizes}/>
            </React.Fragment>
        )
    }
}

const authCondition = (authUser: any) => remergeCondition(authUser);


export default withAuthorization(authCondition)(withRouter(connect(
    mapStateToProps,
    mapDispatchToProps,
)(BannerPreviewerContainer)));


const genTemplate = (selectedSizes: string[], output: string) => {

    let doc = escape(output.replace(/href=""/g, 'data-h=""'));
    let sizes = JSON.stringify(selectedSizes);

    return `
<!DOCTYPE html>
<html>

<head>
    <meta charset="utf-8">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <link rel="icon" href="https://www.remerge.io/favicon.ico">

    <script src="https://cdnjs.cloudflare.com/ajax/libs/react/16.12.0/umd/react.production.min.js"></script>
    <script src="https://cdnjs.cloudflare.com/ajax/libs/react-dom/16.11.0/umd/react-dom.production.min.js"></script>

    <!-- Don't use this in production: -->
    <script src="https://cdnjs.cloudflare.com/ajax/libs/babel-standalone/6.26.0/babel.min.js"></script>
    <title>REMERGE - Banner Preview</title>
</head>
<body>
<style>

    @font-face {
        font-family: 'gt_americaregular';
        src: url('https://assets.remerge.io/dpa/gt-america-regular-webfont.woff2') format('woff2'),
        url('https://assets.remerge.io/dpa/gt-america-regular-webfont.woff') format('woff');
        font-weight: normal;
        font-style: normal;
    }

    body {
        background-color: #e0e4ef;
        font-family: 'gt_americaregular', sans-serif;
        margin: 0px;
        color: #1d1d1f;
    }

    h3 {
        font-size: 18px;
        margin: 10px 10px 10px 0;
        font-family: 'gt_americaregular';
        font-weight: normal;
        font-style: normal;
    }

    header {
        margin: 0px;
        padding: 0px;
        height: 80px;
        border-bottom: 1px solid #1d1d1f;
        background-color: #fff;
        margin-bottom: 110px;
    }

    header .logo {
        background-image: url('https://i.imgur.com/DdeYG1B.png');
        background-size: 80%;
        background-position: center center;
        width: 380px;
        height: 80px;
        top: 20px;
        background-repeat: no-repeat;
        padding: 0;
        margin: 0px 30px 30px 5px;
    }

    header .headline {
        display: inline;
        position: absolute;
        left: 240px;
        top: 20px;
        font-size: 24px;
        color: #1d1d1f;
    }

    .button {
        display: inline-block;
        position: absolute;
        left: 40px;
        top: 120px;
        font-size: 20px;
        background-color: rgba(0,0,0,0);
        color: #1d1d1f;
        border: 1px solid;
        border-color: #1d1d1f;
        padding: 10px 14px 10px 14px;
        transition: 0.5s;
        cursor: pointer;
    }

    .button:hover {
        box-shadow: 5px 5px 0px rgba(0, 0, 0, 1);
    }

    .footer {
        position: fixed;
        bottom: 0px;
        height: 30px;
        width: 100%;
        background-color: #ffffff;
        opacity: 0.9;
        padding: 10px;
    }

    .footer .left {
        position: relative;
        margin-left: 20px;
        color: #1d1d1f;
        top: -10px;
    }

    .footer .right {
        position: relative;
        text-align: right;
        width: 100vw;
        bottom: 44px;
        left: -30px;
    }

    .footer .right a {
        color: #1d1d1f;
        text-decoration: none;
    }

    .iframeContainer {
        overflow: auto;
        width: 100%;
        padding: 0 30px 30px 40px;
        background-size: 20px 20px;
    }

    .iframe {
        border: 1px solid;
        margin-bottom: 30px;
    }


</style>

<header>
    <a href="https://remerge.io/" target="_blank">
        <div class="logo"></div>
    </a>
</header>

<div id="app">
</div>



<div class="footer">
    <div class="left">
        <p>©Remerge GmbH, 2020</p>
    </div>
    <div class="right">
        <a target="_blank" href="https://remerge.io">Contact Us</a>
    </div>
</div>

<script type="text/babel" >


const refresh = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const ts = + new Date();
    urlParams.set("v", ts.toString())
    window.location.search = urlParams.toString() 
}

if (!window.location.search) {
    refresh()
}

class App extends React.Component {
    refresh = () => {
        refresh()
    }
  render() {
    return (
      <div className={"iframeContainer"}>
        <button className="button primary" onClick={this.refresh}>RELOAD CREATIVES</button>
      {
          this.props.sizes.map((size) => {
            let [width, height] = size.split("x");
            let doc = this.props.output;
            return (
                <div className={"bannerPreviewerBox"} key={size} title={size}>
                    <h3>{size}</h3>
                    <div>
                        <iframe
                            className={"iframe"}
                            srcDoc={unescape(doc)}
                            width={width}
                            height={height}
                        >
                        </iframe>
                    </div>
                </div>
            )
        })
        }
      </div>
    );
  }
}
const domContainer = document.querySelector('#app');
ReactDOM.render(<App sizes={${sizes}} output={"${doc}"}/>, domContainer);
</script>
</body>
</html>
`
    
    
};

const genNewTemplate = (selectedSizes: string[], output: string) => {

    let doc = escape(output.replace(/href=""/g, 'data-h=""'));
    let sizes = JSON.stringify(selectedSizes);

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <link rel="icon" href="https://www.remerge.io/favicon.ico">
    <link rel="stylesheet" href="https://assets.remerge.io/dpa/Ad-Preview/new-styles.css">

    <script crossorigin src="https://unpkg.com/react@17/umd/react.development.js"></script>
    <script crossorigin src="https://unpkg.com/react-dom@17/umd/react-dom.development.js"></script>

    <!-- Don't use this in production: -->
    <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
    
    <title>REMERGE - Banner Preview</title>
</head>
<body>
<header>
    <a href="https://remerge.io/" target="_blank" class="logo">
        <h1>Ad Preview Tool</h1>
    </a>

    <ul class="internal-links">
      <li><a href="#" class="active">
        <span class="internal-icon icon-view">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M10 19.63a9.63 9.63 0 1 0-6.82-2.82A9.65 9.65 0 0 0 10 19.63Z" class="circle"/>
            <path class="pict" d="m11.23 5.34-.41-.08h-.38a3.87 3.87 0 0 0-3.87 3.87 3.92 3.92 0 0 0 .83 2.39l-2.82 2.82a.43.43 0 0 0-.14.32.51.51 0 0 0 .13.33.47.47 0 0 0 .27.1.44.44 0 0 0 .38-.09l2.83-2.83a3.87 3.87 0 0 0 6.26-3 3.9 3.9 0 0 0-3.08-3.83Zm2.16 3.79a3 3 0 1 1-3-3 3 3 0 0 1 3 3Z"/>
          </svg>
        </span><span>Preview</span></a></li>
      <li><a href="https://remerge-se.web.app/templates" target="_blank"><span class="internal-icon icon-edit">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <path d="M10 19.63a9.63 9.63 0 1 0-6.82-2.82A9.65 9.65 0 0 0 10 19.63Z" class="circle"/>
          <path class="pict" d="M5.811 11.067 13.42 3.46l2.588 2.588-7.609 7.608z" style="stroke-linecap:round;stroke-linejoin:round"/>
          <path class="pict" d="m8.4 13.66-4.14 1.55 1.56-4.14 2.58 2.59z" style="stroke-linecap:round;stroke-linejoin:round"/>
        </svg>
      </span><span>Edit</span></a></li>
      <li><a href="ad.html" target="_blank"><span class="internal-icon icon-raw">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <path d="M10 19.63a9.63 9.63 0 1 0-6.82-2.82A9.65 9.65 0 0 0 10 19.63Z" class="circle"/>
        <path class="pict" d="M6.73 13.06 3.87 10.2l2.86-2.87M13.5 7.26l2.86 2.86-2.86 2.86M11.31 5.25 9 14.83" style="stroke-linecap:round;stroke-linejoin:round"/>
        </svg>
      </span><span>Raw</span></a></li>
    </ul>

    <nav>

        <ul>
          <li><div class="checkbox"><input type="checkbox" id="c1" onclick="changeClass('show-square')" checked/><label for="c1"><div class="icon icon-square"></div><div class="label"><div class="name">Square</div><div class="aspect-ratio">1:1</div></div></label></div></li>
          <li><div class="checkbox"><input type="checkbox" id="c2" onclick="changeClass('show-banner')" checked/><label for="c2"><div class="icon icon-banner"></div><div class="label"><div class="name">Banner</div><div class="aspect-ratio">32:5</div></div></label></div></li>
          <li><div class="checkbox"><input type="checkbox" id="c3" onclick="changeClass('show-portrait')" checked/><label for="c3"><div class="icon icon-portrait"></div><div class="label"><div class="name">Portrait</div><div class="aspect-ratio">9:16</div></div></label></div></li>
        </ul>

        <div class="custom-size">
          <label>Custom size</label>
          <input type="text" id="widthInput" value="650" onclick="this.value=''"/>px : <input type="text" id="heightInput" value="550" onclick="this.value=''"/>px
          <button id="resizeButton">Resize Element</button>
        </div> 

    </nav>
</header>


<div id="app" class="show-square show-native show-banner show-portrait">

<div id="customSizeBox">
     <iframe class="iframe" src="https://assets.remerge.io/dpa/Ad-Preview/ad.html">  
    </iframe>
</div>

<div class="iframeContainer">


<div class="phone square live-dummy-content">
    <div class="inner">
  <div class="phone-screen">
    
      <div id="new-square"></div>

  </div>
        </div>
</div>

<div class="phone banner live-dummy-content">
<div class="inner">
  <div class="phone-screen">
    <div id="new-banner"></div>  
  </div>
    </div>
</div>

<div class="phone fullscreen live-dummy-content">
    <div class="inner">
  <div class="phone-screen">
    <div id="new-full"></div>
  </div>
    </div>
</div>

</div>

</div>
<div id="root">
</div>
<script type="text/babel" >
const refresh = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const ts = + new Date();
    urlParams.set("v", ts.toString())
    window.location.search = urlParams.toString() 
}

if (!window.location.search) {
    refresh()
}

class App extends React.Component{
    refresh = () => {
        refresh()
    }
    render(){
        let [width, height] = this.props.sizes[0].split("x");
        let document = this.props.output;
        let cName = this.props.classN;
        return(
                <iframe
                    className={cName}
                    srcDoc={unescape(document)}
                    >
                </iframe>
        )
    }
}

const customContainer = document.getElementById('customSizeBox')
ReactDOM.render(<App sizes={${sizes}} output={"${doc}"} classN={"iframe"}/>, customContainer);

const squareContainer = document.getElementById('new-square')
ReactDOM.render(<App sizes={${sizes}} output={"${doc}"} classN={"iframe"}/>, squareContainer);

const bannerContainer = document.getElementById('new-banner')
ReactDOM.render(<App sizes={${sizes}} output={"${doc}"} classN={"iframe"}/>, bannerContainer);

const fullContainer = document.getElementById('new-full')
ReactDOM.render(<App sizes={${sizes}} output={"${doc}"} classN={"iframe"}/>, fullContainer);

</script>
<script>
    function changeClass(className) {
      var element = document.getElementById("app");
      
      element.classList.toggle(className);
        
    if (element.classList.contains("customSize")){
        element.classList.remove("customSize");
    }         
}
</script>
<script>
      document.getElementById('resizeButton').addEventListener('click', function() {
            var width = document.getElementById('widthInput').value;
            var height = document.getElementById('heightInput').value;

            var element = document.getElementById('customSizeBox');
          
          element.style.width = width + 'px';
            element.style.height = height + 'px';
          
          uncheck("c1");
          uncheck("c2");
          uncheck("c3");
           
          document.getElementById('app').classList.add('customSize');
        
        });
    
    function uncheck(theId){
        element = document.getElementById(theId);
        
        if (element.checked) {
            element.click();
        } else {
            return false;
        }
    }    
</script>
</body>
</html>
`    
};

